import { createClient } from '@supabase/supabase-js';

// 環境変数からSupabaseクライアントを作成
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://dukuvttucohydjyfeefp.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR1a3V2dHR1Y29oeWRqeWZlZWZwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDcyODEwNTMsImV4cCI6MjA2Mjg1NzA1M30.F_61rPcYX_U6e6iGTiuWoBJbwzq14D8E2-QufnBTyVU';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.error('[Supabase] 環境変数が設定されていません');
  console.error('[Supabase] URL:', SUPABASE_URL ? 'Set' : 'Not set');
  console.error('[Supabase] Key:', SUPABASE_ANON_KEY ? 'Set' : 'Not set');
} else {
}

// セッション管理設定
const AUTH_CONFIG = {
  SESSION_CHECK_INTERVAL: 5 * 60 * 1000, // 5分
  WARNING_BEFORE_EXPIRE: 5 * 60 * 1000,  // 期限5分前に警告
  AUTO_SAVE_INTERVAL: 60 * 1000,         // 1分ごとに自動保存
  MAX_RETRY_ATTEMPTS: 3                  // 最大リトライ回数
};

// PKCEフローを維持しつつ安定性を確保
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    flowType: 'pkce',           // PKCEフロー（セキュア）
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage, // 明示的に指定
    storageKey: 'supabase.auth.token'
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'apikey': SUPABASE_ANON_KEY
    }
  }
});

export { AUTH_CONFIG };

// ユーザーアクティビティトラッキング
let lastActivity = Date.now();

// アクティビティ更新
const updateActivity = () => {
  lastActivity = Date.now();
};

// ユーザー操作を監視
if (typeof document !== 'undefined') {
  document.addEventListener('click', updateActivity);
  document.addEventListener('keypress', updateActivity);
  document.addEventListener('scroll', updateActivity);
  document.addEventListener('mousemove', updateActivity);
}

// インテリジェントなセッション更新
export const ensureValidSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (!session) {
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      return data.session;
    }
    
    // セッションの有効期限をチェック
    const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
    const now = Date.now();
    
    // 期限切れまでの時間を計算
    const timeUntilExpiry = expiresAt - now;
    
    // アクティブユーザーのみ更新（10分以内に操作があれば）
    const inactiveDuration = now - lastActivity;
    const isUserActive = inactiveDuration < 10 * 60 * 1000;
    
    if (timeUntilExpiry < AUTH_CONFIG.WARNING_BEFORE_EXPIRE && isUserActive) {
      const { data, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      return data.session;
    }
    
    return session;
  } catch (error) {
    throw error;
  }
};

// エラーリカバリー付き保存
export const saveWithRetry = async (
  saveFunction: () => Promise<any>,
  maxRetries: number = AUTH_CONFIG.MAX_RETRY_ATTEMPTS
): Promise<any> => {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // セッション確認
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        // セッション再取得試行
        await supabase.auth.refreshSession();
      }
      
      // 保存実行
      const result = await saveFunction();
      return result;
      
    } catch (error) {
      lastError = error as Error;

      if (attempt < maxRetries - 1) {
        // 指数バックオフで待機
        const waitTime = Math.pow(2, attempt) * 1000;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
    }
  }
  
  // 最終的に失敗した場合
  throw new Error(`${maxRetries}回の試行後も保存に失敗: ${lastError?.message}`)
};